import React from "react";
import {
  Box,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import type { DatasetGroup } from "@/utils/group-datasets";
import type { Dataset } from "@/models/Dataset";

interface DatasetSelectorProps {
  datasetGroups: DatasetGroup[];
  selectedDatasetId: string;
  isAutoSelected: boolean;
  searchTerm: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isFetching: boolean;
  hasMore: boolean;
  observerTarget: React.RefObject<HTMLDivElement | null>;
  onSelect: (datasetId: string, datasetVersionId?: number | null) => void;
}

const isInUseGroup = (label: string) => label === "In Use in This Dashboard";

const DatasetSelector: React.FC<DatasetSelectorProps> = ({
  datasetGroups,
  selectedDatasetId,
  isAutoSelected,
  searchTerm,
  onSearchChange,
  isFetching,
  hasMore,
  observerTarget,
  onSelect,
}) => {
  const totalDatasets = datasetGroups.reduce(
    (sum, g) => sum + g.datasets.length,
    0,
  );

  const noResults = !isFetching && totalDatasets === 0 && !searchTerm;

  if (noResults) {
    return (
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No datasets available.{" "}
          <Typography
            component="a"
            href="/datasets"
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            color="primary"
            sx={{ textDecoration: "underline", cursor: "pointer" }}
          >
            Create a dataset in Dataset Configuration.
          </Typography>
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
        Dataset*
      </Typography>

      <TextField
        size="small"
        fullWidth
        placeholder="Search datasets..."
        value={searchTerm}
        onChange={onSearchChange}
        sx={{ mb: 1 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          maxHeight: 280,
          overflowY: "auto",
        }}
      >
        {totalDatasets === 0 && searchTerm && !isFetching ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ p: 2, textAlign: "center" }}
          >
            No datasets match "{searchTerm}"
          </Typography>
        ) : (
          <>
            {datasetGroups.map((group) => (
              <Box key={group.label}>
                {/* Section header */}
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    display: "block",
                    px: 2,
                    py: 0.75,
                    backgroundColor: "#e8eaed",

                    color: "#000",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  {group.label}
                </Typography>

                {/* Dataset rows */}
                {group.datasets.map((dataset: Dataset) => {
                  const isSelected = dataset.id === selectedDatasetId;
                  const inUse = isInUseGroup(group.label);

                  return (
                    <Box
                      key={dataset.id}
                      onClick={() =>
                        onSelect(dataset.id, dataset.datasetVersionId ?? null)
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1.25,
                        cursor: "pointer",
                        color: isSelected
                          ? "#1F68DC"
                          : "var(--color-hp-text-day-primary)",
                        backgroundColor: isSelected ? "#E3F2FD" : "transparent",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&:last-child": { borderBottom: "none" },
                        "&:hover": {
                          backgroundColor: isSelected
                            ? "var(--color-hp-primary-light, #E3F2FD)"
                            : "var(--color-hp-background-hover, #F5F5F5)",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={isSelected ? 500 : 400}
                      >
                        {dataset.name}
                      </Typography>

                      {inUse && (
                        <Chip
                          icon={<CheckIcon sx={{ fontSize: 14 }} />}
                          label="In Use"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.7rem",
                            backgroundColor: "#E8F5E9",
                            color: "#2E7D32",
                            "& .MuiChip-icon": { color: "#2E7D32" },
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            ))}

            {/* Infinite scroll sentinel */}
            {hasMore && (
              <Box ref={observerTarget} sx={{ p: 1, textAlign: "center" }}>
                {isFetching && <CircularProgress size={16} />}
              </Box>
            )}
          </>
        )}
      </Box>

      {isAutoSelected && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block", fontStyle: "italic" }}
        >
          Auto-selected (currently in use)
        </Typography>
      )}
    </Box>
  );
};

export default DatasetSelector;
